import React from "react";
import { Link } from "react-router-dom";
import stats from "../../data/atlasStats.json";
import { REVIEWER, EDITORIAL_PATH } from "../../config/eeat";
import "../../styles/Home5.css";

// Where the evidence comes from — the E-E-A-T section, and the one that has to
// be scrupulously literal. Herbal content is YMYL: overstating the corpus here
// is the fastest way to lose the trust the rest of the page is arguing for.
//
// The source list is baked from kb_sources at build time and includes ONLY
// sources that actually carry edges. Two more are registered in the database
// with zero edges; they are deliberately not mentioned until they carry data.

const n = (v) => String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const EvidenceSection = () => (
  <section className="sv-dark sv-section evidence">
    <div className="sv-wrap">
      <h2 className="sv-h2 sv-center evidence__title">
        Where the evidence comes from
      </h2>
      <p className="sv-body sv-center evidence__intro">
        We don't write herbal claims. We re-model what published reference works
        already record, keep the citation attached, and show you the grade.
        Every source named — and every one of them something you can go and
        check yourself.
      </p>

      <div className="evidence__grid">
        {stats.sources.map((s) => (
          <article key={s.code} className="sv-card evidence__source">
            <p className="evidence__count">{n(s.edges)}</p>
            <h3 className="sv-h5">{s.title}</h3>
            <p className="evidence__meta">
              {s.authors ? `${s.authors} · ` : ""}
              {s.year}
            </p>
            <p className="sv-body evidence__body">
              A compilation, not an opinion: it indexes the primary literature
              rather than asserting its own claims, which is why every
              relationship arrives with citations already attached.
            </p>
          </article>
        ))}

        {/* The answer to "it's only one book". Duke's Handbook is an INDEX into
            the works below — the graph carries their citation codes on every
            edge, resolved to kb_source_refs. Each name here is verifiable in
            that table (KOM = Blumenthal 1998 Commission E, WHO = World Health
            Organization 1999, M28/M29 = Martindale, PHR = PDR for Herbal
            Medicine), so this card adds authority without inventing any. Do not
            add a reference to this list that is not actually in kb_source_refs. */}
        <article className="sv-card evidence__source">
          <p className="evidence__count">{n(stats.sourceRefs)}</p>
          <h3 className="sv-h5">Named references behind the claims</h3>
          <p className="evidence__meta">
            Commission E · WHO · Martindale · PDR for Herbal Medicine
          </p>
          <p className="sv-body evidence__body">
            The claims trace past the compilation to the works themselves — the
            German Commission E monographs, WHO, Martindale, the PDR and{" "}
            {n(stats.sourceRefs - 4)} more. Every relationship carries the codes
            of the references behind it, so you can go and read the original.
          </p>
        </article>

        {/* The two sources that carry no graph edges but do real work, and are
            worth naming precisely BECAUSE they are independent of Duke — one
            book cited three times is a weaker claim than three institutions.
            Both are hard-coded rather than baked because neither produces
            edges, which is what buildStats counts. */}
        <article className="sv-card evidence__source">
          <p className="evidence__count">{n(stats.safety.herbsChecked)}</p>
          <h3 className="sv-h5">Drugs and Lactation Database (LactMed)</h3>
          <p className="evidence__meta">
            US National Library of Medicine · public domain
          </p>
          <p className="sv-body evidence__body">
            Pregnancy and breastfeeding safety for every plant in the atlas. A
            US Government work, so where it has something to say we can show you
            its exact words rather than our summary of them.
          </p>
        </article>

        <article className="sv-card evidence__source">
          <p className="evidence__count">{n(23081)}</p>
          <h3 className="sv-h5">World Flora Online</h3>
          <p className="evidence__meta">
            The global plant-name authority · CC0
          </p>
          <p className="sv-body evidence__body">
            Botanical synonyms, so ashwagandha and <em>Withania somnifera</em>{" "}
            resolve to one plant. Unglamorous, and the reason herbal sources
            contradict each other when it is missing.
          </p>
        </article>

        <article className="sv-card evidence__source evidence__source--note">
          <h3 className="sv-h5">What we don't do</h3>
          <ul className="evidence__list">
            <li>We don't reproduce the source texts — we cite them.</li>
            <li>
              We don't present traditional use as though it were clinical
              evidence.
            </li>
            <li>
              We don't surface the {stats.unsafeHerbs} plants rated unsafe for
              self-administration unless you ask.
            </li>
            <li>
              We don't publish a condition page without at least three plants
              behind it.
            </li>
          </ul>
        </article>

        <article className="sv-card evidence__source evidence__source--review">
          <h3 className="sv-h5">Who checks it</h3>
          <p className="sv-body evidence__body">
            Editorial content is reviewed by {REVIEWER.name}. The extraction
            pipeline is re-runnable and checked against the source works, and we
            publish how we work.
          </p>
          <Link className="evidence__link" to={EDITORIAL_PATH}>
            Our editorial process →
          </Link>
        </article>
      </div>

      <p className="evidence__disclaimer">
        This information is for general wellness and educational purposes only
        and is not medical advice. Traditional use is not evidence of
        effectiveness. Consult a qualified healthcare provider before using any
        herb — especially if you are pregnant, nursing, or taking medication.
      </p>
    </div>
  </section>
);

export default EvidenceSection;
